/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import ChartBarIcon from '@atlaskit/icon/core/chart-bar';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { BUILTIN_TOOLBAR_KEYS } from '@atlaskit/native-embeds-common/constants';
import { type ManifestEditorToolbarActions } from '@atlaskit/native-embeds-common/types';
import { createEditorToolbarActions } from '@atlaskit/native-embeds-common/utils';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import LoadingOverlay from '@atlassian/native-embeds-core/loading-overlay';
import { getInitialThemeParam } from '@atlassian/native-embeds-core/theme';
import {
	type Experience,
	type ExperienceManifest,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types';
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core/types/features/main';

import { AVPVIZ_EXPERIENCE_MANIFEST_ID } from './manifest-id';

const avpvizIcons = [ChartBarIcon];

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const AVPVIZ_CHART_URL_PATTERN = new RegExp(
	`/(?:avpviz|dashboards)/c/${UUID}/w/${UUID}/d/${UUID}/(?:chart|widget)/${UUID}/?(?:[?#].*)?$`,
);

const isAvpvizChartUrl = (url: string): boolean =>
	fg('platform_avp_viz_confluence_native_embed') && AVPVIZ_CHART_URL_PATTERN.test(url);

/**
 * Experience manifest for a shared AVPViz chart.
 *
 * This claims only canonical shared chart URLs while its rollout gate is enabled.
 * The linking-platform resolver supplies the title and preview used by the host overlay.
 */
export const AvpvizExperienceManifest: {
	readonly handleChildCommands: (
		_experience: Experience,
		_command: NativeEmbedChildCommand,
	) => void;
	readonly id: typeof AVPVIZ_EXPERIENCE_MANIFEST_ID;
	readonly isActive: () => true;
	readonly isUrlMatch: (url: string) => boolean;
	readonly resolveViaLinkingPlatform: true;
	readonly supportedFeatures: Set<string>;
	readonly transformUrl: (url: string, isFullScreen?: boolean) => string;
	readonly uiConfig: {
		readonly appearance: 'overlay';
		readonly editorToolbarActions: () => ManifestEditorToolbarActions<any>;
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type
		readonly editorToolbarHandlers: {};
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type
		readonly headerControls: {};
		readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element;
		readonly titleIcon: {
			(props: NewCoreIconProps): JSX.Element;
			displayName: string;
		};
		readonly useResolvedTitleIcon: true;
	};
} = {
	id: AVPVIZ_EXPERIENCE_MANIFEST_ID,
	handleChildCommands: (_experience: Experience, _command: NativeEmbedChildCommand): void => {
		// No AVPViz-specific child commands yet.
	},
	isUrlMatch: isAvpvizChartUrl,
	isActive: (): true => true,
	resolveViaLinkingPlatform: true,
	supportedFeatures: new Set<NativeEmbedFeature>([...allNativeEmbedCoreFeatures]),
	transformUrl: (url: string, isFullScreen?: boolean): string => {
		const urlObj = new URL(url);
		if (!isFullScreen) {
			urlObj.searchParams.set('hideBorder', 'true');
		}
		urlObj.searchParams.set('nativeEmbedView', 'true');

		const initialTheme = getInitialThemeParam();
		if (initialTheme) {
			urlObj.searchParams.set('themeState', initialTheme);
		}

		return urlObj.toString();
	},
	uiConfig: {
		headerControls: {},
		editorToolbarActions: (): ManifestEditorToolbarActions<any> =>
			createEditorToolbarActions({
				items: [
					BUILTIN_TOOLBAR_KEYS.EMBED,
					BUILTIN_TOOLBAR_KEYS.EDIT,
					BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
				],
				moreItems: [
					BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE,
					BUILTIN_TOOLBAR_KEYS.COPY,
					BUILTIN_TOOLBAR_KEYS.DELETE,
				],
			}),
		editorToolbarHandlers: {},
		renderLoadingComponent: (props: LoadingComponentProps): JSX.Element => (
			<LoadingOverlay
				animationDisabled={props.animationDisabled}
				icons={avpvizIcons}
				testId={props.testId}
			/>
		),
		titleIcon: ChartBarIcon,
		useResolvedTitleIcon: true,
		appearance: 'overlay',
	},
} as const satisfies ExperienceManifest;
